import type { GetServerSideProps, GetServerSidePropsResult } from "next";
import type { IConfig } from "config/IConfig";
import { ServiceProvider } from "contexts/ServiceContext";
import { ProfilePageTypes } from "enums/ProfilePageTypes";
import useCreateServices from "hooks/useCreateServices";
import type { ITexts } from "translations/ITexts";
import type { IRequestNeededForServices } from "types/IRequestNeededForServices";
import { getServerSidePropertiesNeededForEveryPage } from "utils/getRequestNeededForServicesFromContext";
import useInitAnalytics from "hooks/useInitAnalytics";
import { QueryClient } from "react-query";
import { dehydrate, DehydratedState } from "react-query/hydration";
import { PROFILE_QUERY_KEY } from "queries/queryKeys";
import createServices from "services/createServices";
import SavedPagesContainer from "components/SavedPagesContainer";
import { useEffect } from "react";
import { GetCatalogsByIdsQuery, GetCatalogsByIdsQueryVariables } from "graphql/schema-types";
import getCatalogsByIdsQuery from "services/gql/queries/getCatalogsByIds";
import { SavedPage } from "types/SavedPage";

interface IProps {
    configs: IConfig;
    texts: ITexts;
    req: IRequestNeededForServices;
    savedPages: SavedPage[];
    dehydratedState: DehydratedState;
}

export default function SavedPages({ configs, req, texts, savedPages }: IProps) {
    const services = useCreateServices(configs, texts, ProfilePageTypes.SAVED, req);

    useInitAnalytics(services);

    useEffect(
        () => {
            services.TiendeoStatsClient.Navigation(0);
            services.TiendeoStatsClient.InitializeCatalogImpression();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <ServiceProvider Services={services}>
            <SavedPagesContainer savedPages={savedPages} />
        </ServiceProvider>
    );
}

export const getServerSideProps: GetServerSideProps<IProps> = async (context) => {
    const { configs, req, texts, res } = getServerSidePropertiesNeededForEveryPage(context);

    const { AuthApiClient, CookiesClient, GqlService, Configs } = createServices(
        configs,
        texts,
        ProfilePageTypes.SAVED,
        req as any,
        res,
    );

    const getFavCookies = CookiesClient.Get("favs");

    let savedPagesInitialArray = [];

    if (getFavCookies) {
        const catalogsFavCookies = getFavCookies.split("#");
        savedPagesInitialArray = catalogsFavCookies.map((catalog) => {
            return catalog.split("|");
        });
    }

    let catalogsIds = savedPagesInitialArray.map((catalog) => {
        return parseInt(catalog[0]);
    });

    const catalogs = await GqlService.request<
        GetCatalogsByIdsQuery,
        GetCatalogsByIdsQueryVariables
    >(getCatalogsByIdsQuery, { catalogsIds });

    const { url_tiendeo, path_catalogs } = Configs;

    const savedPages: SavedPage[] = savedPagesInitialArray.reduce(
        (acc: SavedPage[], catalogPage) => {
            const [catalogId, pageNumber] = catalogPage;
            const savedPage = catalogs.CatalogsByIds.nodes.find(
                (catalog) => catalog.id === parseInt(catalogId),
            );
            if (savedPage) {
                const urlTemplate = savedPage.imageTemplate.replace("{size}", "med");
                const savedPageImg = urlTemplate.replace("{num_page}", pageNumber.padStart(5, "0"));
                const pageUrl = `${url_tiendeo}${path_catalogs}/${savedPage?.id}?pagina=${pageNumber}&city=no_city&search=PROFILE&refPageType=${ProfilePageTypes.SAVED}&view=offers-search`;
                acc.push({
                    ...savedPage,
                    img: savedPageImg,
                    url: pageUrl,
                    page: parseInt(pageNumber),
                });
            }
            return acc;
        },
        [],
    );

    const queryClient = new QueryClient();

    await queryClient.prefetchQuery(PROFILE_QUERY_KEY, () => AuthApiClient.User.GetProfile());

    return {
        props: {
            configs,
            req,
            texts,
            savedPages: savedPages,
            dehydratedState: dehydrate(queryClient),
        },
    };
};
